import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL as CONFIG_API_URL } from "../../config";
import { Upload, Plus, Trash, Trash2, Search, Calendar, Play, Clock, CheckCircle2, XCircle, ChevronRight, Activity, Filter, Settings, FileText, X, ChevronDown, Terminal, Database, Zap, Bot } from "lucide-react";


// Added format helper
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  const pad = (n) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const getElapsedTime = (task) => {
  if (task.status !== 'running') return '-';
  const start = task.lastRunAt ? new Date(task.lastRunAt) : new Date(task.createdAt);
  if (isNaN(start.getTime())) return '-';
  const diffMs = Date.now() - start.getTime();
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}小时${minutes % 60}分钟`;
  return `${minutes}分钟`;
};


const API_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

const MOCK_PROJECTS = [
  { id: 1, name: '特斯拉 (Tesla)' },
  { id: 2, name: '蔚来汽车 (NIO)' },
  { id: 3, name: '理想汽车 (Li Auto)' },
];

const engineLogos = {
  /* INJECTED_MB_PC_LOGOS_v2 */
  '豆包': '/icons/doubao.png',
  '豆包(PC)': '/icons/doubao.png',
  '豆包(MB)': '/icons/doubao.png',
  '豆包（PC）': '/icons/doubao.png',
  '豆包（MB）': '/icons/doubao.png',
  'doubao': '/icons/doubao.png',
  '腾讯元宝': '/icons/yuanbao.png',
  '腾讯元宝(PC)': '/icons/yuanbao.png',
  '腾讯元宝(MB)': '/icons/yuanbao.png',
  '腾讯元宝（PC）': '/icons/yuanbao.png',
  '腾讯元宝（MB）': '/icons/yuanbao.png',
  '通义千问': '/icons/tongyi.png',
  '通义千问(PC)': '/icons/tongyi.png',
  '通义千问(MB)': '/icons/tongyi.png',
  '通义千问（PC）': '/icons/tongyi.png',
  '通义千问（MB）': '/icons/tongyi.png',
  '通义': '/icons/tongyi.png',
  '文心一言': '/icons/wenxin.png',
  '文心一言(PC)': '/icons/wenxin.png',
  '文心一言(MB)': '/icons/wenxin.png',
  'Deepseek': '/icons/deepseek.png',
  'DeepSeek': '/icons/deepseek.png',
  'deepseek': '/icons/deepseek.png',
  'Kimi': '/icons/kimi.png',
  'kimi': '/icons/kimi.png',
  '元宝': '/icons/yuanbao.png',
  '百度AI搜索': '/icons/baidu.png',
  '百度(pc)': '/icons/baidu.png',
  '百度(PC)': '/icons/baidu.png',
  '百度(移动)': '/icons/baidu.png',
  '百度(MB)': '/icons/baidu.png',
  '小红书AI': '/icons/xiaohongshu.png',
  '抖音AI': '/icons/douyin.png',
  '小红书': '/icons/xiaohongshu.png',
  'xhs': '/icons/xiaohongshu.png',
  '微信搜一搜': '/icons/wechat.png',
  '微信公众号': '/icons/wechat.png',
  '搜狗微信': '/icons/wechat.png',
  '微信': '/icons/wechat.png',
  'sgwxpc': '/icons/wechat.png',
  '知乎': '/icons/zhihu.png',
  '微博': '/icons/weibo.png',
  '抖音': '/icons/douyin.png',
  'B站': '/icons/bilibili.png',
  '头条': '/icons/toutiao.png',
  '今日头条': '/icons/toutiao.png',
};

const MOCK_TASKS = [
  {
    id: 'TSK-10024',
    projectName: '特斯拉 (Tesla)',
    type: 'one-time', // one-time, scheduled
    status: 'running', // running, completed, failed, scheduled
    progress: 45,
    engines: ['Deepseek', '文心一言', 'Kimi'],
    personas: ['新锐白领', '硬核技术控'],
    createdAt: '2026-04-14 10:30:00',
    lastRunAt: '2026-04-14 10:30:00',
    timeElapsed: '12m 30s'
  },
  {
    id: 'TSK-10023',
    projectName: '蔚来汽车 (NIO)',
    type: 'scheduled',
    scheduleFreq: '每周一 08:00',
    status: 'scheduled',
    progress: 0,
    engines: ['全部 8 个引擎'],
    personas: ['全部 8 个人设'],
    createdAt: '2026-04-10 14:20:00',
    lastRunAt: '2026-04-13 08:00:00',
    timeElapsed: '-'
  },
  {
    id: 'TSK-10022',
    projectName: '理想汽车 (Li Auto)',
    type: 'one-time',
    status: 'completed',
    progress: 100,
    engines: ['豆包(PC)', '豆包(MB)', '通义千问'],
    personas: ['精致宝妈'],
    createdAt: '2026-04-13 16:00:00',
    lastRunAt: '2026-04-13 16:00:00',
    timeElapsed: '4m 12s'
  }
];

export default function SeoTasks({ system = "seo" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [importTab, setImportTab] = useState('api');
  const [importFile, setImportFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;
  const [expandedTasks, setExpandedTasks] = useState({});
  const [showDeleted, setShowDeleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const toggleExpand = (id) => setExpandedTasks(prev => ({ ...prev, [id]: !prev[id] }));
  const [projects, setProjects] = useState([]);
  const [availableEngines, setAvailableEngines] = useState([]);
  const [availablePersonas, setAvailablePersonas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newTaskForm, setNewTaskForm] = useState({
    projectId: '',
    taskType: 'one-time', // one-time | scheduled
    freq: 'daily', freqTime: '08:00', freqDayOfWeek: '1', freqDayOfMonth: '1',
    engines: [],
    xhsMode: '2', // 0: AIO, 1: 笔记, 2: 全部 // [] means all
    personas: [], // [] means all
    fetchMode: 'rpa', needScreenshot: false, seoDepth: 10, xhsSort: '100'
  });


  
  
  const [taskDetail, setTaskDetail] = useState(null);
  
  const fetchTaskDetail = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks/${taskId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTaskDetail(data.data);
      }
    } catch (err) {
      console.error("Failed to fetch task details:", err);
    }
  };

  const handleEngineFallback = async (taskId, engineId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks/${taskId}/fallback-engine`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ engineId })
      });
      const data = await res.json();
      if (data.success) {
        alert('已启动 API 补足: ' + (data.message || ''));
        fetchTaskDetail(taskId);
      } else {
        alert('启动补足失败: ' + (data.message || ''));
      }
    } catch (err) {
      console.error('Engine fallback error:', err);
      alert('请求补足失败');
    }
  };

  useEffect(() => {
    if (selectedTask && selectedTask.id) {
      setTaskDetail(null); // loading state
      const token = localStorage.getItem('token');
      fetch(`${API_URL}/geo-tasks/${selectedTask.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          setTaskDetail(res.data);
        }
      })
      .catch(e => console.error(e));
    } else {
      setTaskDetail(null);
    }
  }, [selectedTask]);

  useEffect(() => {
    fetchTasks();
  }, [showDeleted]);
  
  useEffect(() => {
    fetchProjects();
    fetchSystemPersonas();
  }, []);

  const [systemPersonasCache, setSystemPersonasCache] = useState([]);

  const fetchSystemPersonas = async () => {
    try {
      const res = await fetch(`${API_URL}/personas/system`);
      if (res.ok) {
        const data = await res.json();
        setSystemPersonasCache(data);
      }
    } catch(e) {}
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/brand-projects?system=${system}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        setProjects(data.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (newTaskForm.projectId && projects.length > 0) {
      const allEngines = system === 'geo' ? ['Deepseek', 'Kimi', '豆包(PC)', '豆包(MB)', '腾讯元宝(PC)', '腾讯元宝(MB)', '通义千问(PC)', '通义千问(MB)', '文心一言', '小红书AI', '抖音AI', '百度AI搜索'] : ['百度', '小红书', '抖音', 'B站', '今日头条', '知乎', '微信公众号', '微博'];
      setAvailableEngines(allEngines);
      
      let availPersonas = systemPersonasCache.map(p => p.name);
      if(availPersonas.length === 0) {
        availPersonas = ['新锐白领', '精致宝妈', 'Z世代尝鲜党', '硬核技术控', '资深中产', '小镇青年', '银发一族', '企业决策者'];
      }
      
      const project = projects.find(p => p.id === parseInt(newTaskForm.projectId));
      let defaultEngines = [];
      let defaultPersonas = [];
      
      if (project) {
        if (project.engineConfigs && project.engineConfigs.length > 0) {
          defaultEngines = project.engineConfigs.filter(e => e.status === 'active'  && e.name !== '百度AI搜索' ).flatMap(e => e.name === '豆包' ? ['豆包(PC)', '豆包(MB)'] : (e.name === '腾讯元宝' || e.name === '元宝') ? ['腾讯元宝(PC)', '腾讯元宝(MB)'] : (e.name === '通义千问') ? ['通义千问(PC)', '通义千问(MB)'] : [e.name]);
        }
        if (project.personas && project.personas.length > 0) {
          const customNames = project.personas.map(p => p.name);
          availPersonas = [...availPersonas, ...customNames];
        }
        
        if (project.activePersonaIds) {
          const activeIds = project.activePersonaIds.split(',').map(id => parseInt(id));
          const allPersonas = [...systemPersonasCache, ...(project.personas || [])];
          defaultPersonas = allPersonas.filter(p => activeIds.includes(p.id)).map(p => p.name);
        } else {
          defaultPersonas = [];
        }
      }
      
      setAvailablePersonas(availPersonas);
      
      setNewTaskForm(prev => ({
        ...prev, 
        engines: defaultEngines.length > 0 ? defaultEngines : [], 
        personas: defaultPersonas
      }));
    } else if (!newTaskForm.projectId) {
      setAvailableEngines([]);
      setAvailablePersonas([]);
      setNewTaskForm(prev => ({...prev, engines: [], personas: []}));
    }
  }, [newTaskForm.projectId, systemPersonasCache, projects]);

  
  
  const forceCompleteTask = async (taskId) => {
    if (!window.confirm("确定要强制结束该任务吗？未完成的部分将被丢弃。")) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks/${taskId}/force-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        fetchTasks();
      } else {
        alert("操作失败");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    if (newStatus === 'stopped' && !window.confirm("确定要终止该任务吗？所有未完成的进度将被取消。")) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks/${taskId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        fetchTasks();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTasks = async () => {

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks?deleted=${showDeleted}&system=${system}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTasks(data.data);
      }
    } catch (error) {
      console.error('获取任务失败:', error);
    } finally {
      setLoading(false);
    }
  };

  
  const handleImportSubmit = async () => {
    if (!newTaskForm.projectId) return alert('请先选择关联的品牌项目');
    if (!importFile) return alert('请选择要导入的 Excel 文件');

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('file', importFile);
    formData.append('projectId', newTaskForm.projectId);
    formData.append('system', 'seo'); // Force SEO system
    if (newTaskForm.dataDate) {
      formData.append('dataDate', newTaskForm.dataDate);
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks/import-mingxi`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        alert(`导入成功！创建了 ${data.taskNo || data.data?.taskNo || '未知'} 任务，导入量：${data.count || data.data?.matrixCount || 0} 条`);
        setIsDrawerOpen(false);
        setImportFile(null);
        fetchTasks();
      } else {
        alert('导入失败: ' + (data.error || '未知错误'));
      }
    } catch (e) {
      console.error(e);
      alert('导入发生网络错误');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitTask = async () => {
    try {
      setIsSubmitting(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          brandProjectId: newTaskForm.projectId,
          system: system,
          type: newTaskForm.taskType,
          frequency: newTaskForm.taskType === 'scheduled' ? JSON.stringify({ type: newTaskForm.freq, time: newTaskForm.freqTime, dayOfWeek: newTaskForm.freqDayOfWeek, dayOfMonth: newTaskForm.freqDayOfMonth }) : null,
          targetEngines: (newTaskForm.engines.length === 0 
            ? availableEngines 
            : newTaskForm.engines).filter(e => e !== '百度AI搜索'),
          targetPersonas: newTaskForm.personas,
          fetchMode: newTaskForm.fetchMode,
          needScreenshot: newTaskForm.needScreenshot,
          xhsMode: newTaskForm.xhsMode, seoDepth: newTaskForm.seoDepth, xhsSort: newTaskForm.xhsSort
        })
      });
      const data = await res.json();
      if (data.success) {
        setIsDrawerOpen(false);
        setNewTaskForm({ ...newTaskForm, projectId: '' });
        fetchTasks();
      } else {
        alert('提交失败: ' + (data.error || '未知错误'));
      }
    } catch (error) {
      console.error('提交任务失败:', error);
      alert('提交异常，请检查网络或日志');
    } finally {
      setIsSubmitting(false);
    }
  };


  
  const formatFrequency = (freqStr) => {
    if (!freqStr) return '未知频率';
    try {
      const freq = JSON.parse(freqStr);
      if (freq.type === 'daily') return `每日 ${freq.time}`;
      if (freq.type === 'multi') {
        if (freq.times && Array.isArray(freq.times)) return `每日多次 (${freq.times.join(', ')})`;
        return '每日多次';
      }
      if (freq.type === 'weekly') {
        const days = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
        return `每周${days[parseInt(freq.dayOfWeek) || 0]} ${freq.time}`;
      }
      if (freq.type === 'monthly') return `每月${freq.dayOfMonth}号 ${freq.time}`;
      return freqStr;
    } catch (e) {
      if (freqStr === 'daily') return '每日';
      if (freqStr === 'weekly') return '每周';
      if (freqStr === 'monthly') return '每月';
      return freqStr;
    }
  };

  const getArrayFromField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    if (typeof field === "string") {
      try {
        const parsed = JSON.parse(field);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === "object" && Array.isArray(parsed.persona)) return parsed.persona;
        return [];
      } catch(e) { return []; }
    }
    if (typeof field === "object" && Array.isArray(field.persona)) return field.persona;
    return [];
  };

  const formatDuration = (start, end) => {
    if (!start) return '';
    const s = new Date(start).getTime();
    const e = end ? new Date(end).getTime() : new Date().getTime();
    const diff = Math.max(0, e - s);
    const mins = Math.floor(diff / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    if (mins > 0) return `${mins}分${secs}秒`;
    return `${secs}秒`;
  };

  const getStatusBadge = (task) => {
    const status = typeof task === 'string' ? task : task.status;
    const fetchMode = typeof task === 'object' ? task.fetchMode : 'api';
    
    switch (status) {
      case 'pending':
        if (fetchMode === 'rpa') {
          return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300"><Activity size={12} className="mr-1.5 animate-pulse" /> 采集处理中</span>;
        } else if (fetchMode === 'offline') {
          return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-300"><Activity size={12} className="mr-1.5 animate-pulse" /> AI清洗研判中</span>;
        } else {
          return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300"><Activity size={12} className="mr-1.5 animate-pulse" /> 抓取中</span>;
        }
      case 'running':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-300"><Activity size={12} className="mr-1.5 animate-pulse" /> 抓取中</span>;
      case 'paused':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300"><Clock size={12} className="mr-1.5" /> 已暂停</span>;
      case 'stopped':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-300"><XCircle size={12} className="mr-1.5" /> 已终止</span>;
      case 'scheduled':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-300"><Clock size={12} className="mr-1.5" /> 定时待机</span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300"><XCircle size={12} className="mr-1.5" /> 异常中断</span>;
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300"><CheckCircle2 size={12} className="mr-1.5" /> 抓取成功</span>;
      default:
        return null;
    }
  };

  const runningTasksCount = tasks.filter(t => t.status === 'running').length;
  const scheduledTasksCount = tasks.filter(t => t.type === 'scheduled').length;
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const completedThisMonthCount = tasks.filter(t => {
    if (t.status !== 'completed') return false;
    const d = new Date(t.createdAt);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  }).length;
  const totalReportsCount = tasks.filter(t => t.status === 'completed').length;

  
  const allParentTasks = tasks.filter(t => {
    if (t.parentId) return false;
    const matchesSearch = searchTerm ? 
      (t.id.toString().includes(searchTerm) || (t.brandProject?.name || '').toLowerCase().includes(searchTerm.toLowerCase())) : true;
    const matchesStatus = statusFilter !== 'all' ? t.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });
  const totalPages = Math.ceil(allParentTasks.length / pageSize) || 1;
  const parentTasks = allParentTasks.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const childrenMap = {};
  tasks.filter(t => t.parentId).forEach(child => {
    if (!childrenMap[child.parentId]) childrenMap[child.parentId] = [];
    childrenMap[child.parentId].push(child);
  });


  const softDeleteTask = async (taskId) => {
    if (!window.confirm("确定要将此任务移入回收站吗？")) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks/${taskId}/soft-delete`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) fetchTasks();
    } catch (e) { console.error(e); }
  };

  const restoreTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks/${taskId}/restore`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) fetchTasks();
    } catch (e) { console.error(e); }
  };

  const hardDeleteTask = async (taskId) => {
    if (!window.confirm("彻底删除后不可恢复！确定要删除吗？")) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/geo-tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) fetchTasks();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="space-y-6 max-w-full p-2 pb-12 relative">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">抓取调度中心</h1>
          <p className="text-sm text-gray-500 mt-1">创建、管理并监控所有 GEO 数据抓取与评估任务</p>
        </div>
        
        <div className="flex gap-2">
          <button onClick={() => {
            const el = document.getElementById('xhs-comments-upload');
            if (el) el.click();
          }} className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors shadow-sm">
            <Upload className="w-4 h-4" />
            <span>导入小红书生态评论</span>
          </button>
          <input
            id="xhs-comments-upload"
            type="file"
            accept=".xlsx, .xls"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              const gbp = localStorage.getItem('globalBrand');
              if (!gbp || gbp === 'all') {
                alert('请先在顶部导航栏选择一个具体的品牌项目');
                e.target.value = null;
                return;
              }
              try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('brandProjectId', gbp);
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/geo-tasks/import-xhs-comments`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                  body: formData
                });
                const resData = await res.json();
                if(resData.success) {
                  alert(`成功导入 ${resData.imported} 条评论。AI后台清洗任务已启动！`);
                  fetchTasks();
                } else {
                  alert(resData.error || '导入失败');
                }
              } catch(err) {
                console.error(err);
                alert('导入失败，请检查文件格式。');
              }
              e.target.value = null;
            }}
          />
          <button 
            onClick={() => setIsDrawerOpen(true)}
            className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors shadow-sm"
          >
            <Plus size={16} />
            <span>发起抓取任务</span>
          </button>
        </div>

      </div>

      {/* Task Summary Metrics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-5 border border-gray-100 dark:border-slate-700 shadow-sm flex items-center">
          <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-500/10 rounded-xl flex items-center justify-center text-indigo-600 dark:text-indigo-400 mr-4">
            <Activity size={24} />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">{runningTasksCount}</div>
            <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">执行中任务</div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-5 border border-gray-100 dark:border-slate-700 shadow-sm flex items-center">
          <div className="w-12 h-12 bg-purple-50 dark:bg-purple-500/10 rounded-xl flex items-center justify-center text-purple-600 dark:text-purple-400 mr-4">
            <Clock size={24} />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">{scheduledTasksCount}</div>
            <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">定时巡检计划</div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-5 border border-gray-100 dark:border-slate-700 shadow-sm flex items-center">
          <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 mr-4">
            <CheckCircle2 size={24} />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">{completedThisMonthCount}</div>
            <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">本月已完成任务</div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-5 border border-gray-100 dark:border-slate-700 shadow-sm flex items-center">
          <div className="w-12 h-12 bg-gray-50 dark:bg-slate-700/50 rounded-xl flex items-center justify-center text-gray-600 dark:text-gray-400 mr-4">
            <FileText size={24} />
          </div>
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-slate-100">{totalReportsCount}</div>
            <div className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">累计生成报告</div>
          </div>
        </div>
      </div>

      {/* Task List */}
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700/50 overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-slate-700/50 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900/20">
          <div className="flex space-x-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
              <input 
                type="text" 
                placeholder="搜索任务编号或项目名称" 
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-9 pr-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm w-64 outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 dark:text-gray-100" 
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-600 dark:text-slate-300 outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">所有状态</option>
              <option value="pending">排队中</option>
              <option value="running">抓取中</option>
              <option value="analyzing">分析中</option>
              <option value="completed">已完成</option>
              <option value="failed">失败</option>
            </select>
            <button 
              onClick={() => setShowDeleted(!showDeleted)}
              className={`px-3 py-2 border rounded-lg text-sm flex items-center transition-colors ${showDeleted ? 'bg-indigo-50 border-indigo-200 text-indigo-600 dark:bg-indigo-500/10 dark:border-indigo-500/30 dark:text-indigo-400' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'}`}>
              <Trash2 className="w-4 h-4 mr-1.5" /> {showDeleted ? '返回正常列表' : '回收站'}
            </button>
          </div>
        </div>

        <table className="w-full text-left text-sm">
          <thead className="bg-white dark:bg-slate-800/40 border-b border-gray-100 dark:border-slate-700">
            <tr className="text-gray-500 dark:text-slate-400">
              <th className="px-6 py-4 font-medium">任务编号 / 项目</th>
              <th className="px-6 py-4 font-medium whitespace-nowrap min-w-[100px]">类型</th>
              <th className="px-6 py-4 font-medium">目标引擎</th>
              <th className="px-6 py-4 font-medium min-w-[160px] whitespace-nowrap">状态 / 进度</th>
              <th className="px-6 py-4 font-medium">时间信息</th>
              <th className="px-6 py-4 font-medium text-right">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700/50">
            {parentTasks.map((task) => (
              <React.Fragment key={task.taskNo || task.id}>
              <tr key={task.taskNo || task.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/30 transition-colors group">
                <td className="px-6 py-4">
                  <div 
                    onClick={() => setSelectedTask(task)}
                    className="font-semibold text-indigo-600 dark:text-indigo-400 cursor-pointer hover:underline"
                  >
                    {task.taskNo || task.id} <span className="text-gray-400 text-xs font-normal">(#{28000000 + task.id})</span>
                  </div>
                  <div className="text-gray-600 dark:text-slate-300 mt-1 font-medium">
                    {task.brandProject?.name || task.projectName}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {task.type === 'one-time' ? (
                    <span className="inline-flex items-center text-gray-600 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded text-xs w-max whitespace-nowrap">单次抓取</span>
                  ) : (
                    <div className="flex flex-col">
                      <span 
                        title={formatFrequency(task.frequency)}
                        className="inline-flex items-center text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-500/10 px-2 py-0.5 rounded text-xs w-max whitespace-nowrap cursor-help transition-colors hover:bg-purple-100 dark:hover:bg-purple-500/20"
                      >
                        <Calendar size={12} className="mr-1" />
                        周期巡检
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1 mb-1.5">
                    {(getArrayFromField(task.engines || task.targetEngines) || []).map((e, i) => {
                      const normalizedEngine = Object.keys(engineLogos).find(key => key.toLowerCase() === (e || "").toLowerCase()) || e;
                      const logoSrc = engineLogos[normalizedEngine];
                      if (e === '豆包(PC)' || e === '豆包' || e === '腾讯元宝(PC)' || e === '腾讯元宝' || e === '通义千问(PC)' || e === '通义千问') {
                         return (
                            <div key={i} className="relative inline-flex flex-col items-center group">
                               <img src={logoSrc} alt={e} title={e} className="w-5 h-5 rounded object-contain bg-white border border-gray-100 dark:border-slate-700 shadow-sm cursor-pointer hover:scale-110 transition-transform" />
                               <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-800 rounded shadow border border-gray-100 dark:border-slate-700 p-[1px] text-gray-500">
                                 <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
                               </div>
                            </div>
                         );
                      }
                      if (e === '豆包(MB)' || e === '腾讯元宝(MB)' || e === '通义千问(MB)') {
                         return (
                            <div key={i} className="relative inline-flex flex-col items-center group">
                               <img src={logoSrc} alt={e} title={e} className="w-5 h-5 rounded object-contain bg-white border border-gray-100 dark:border-slate-700 shadow-sm cursor-pointer hover:scale-110 transition-transform" />
                               <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-800 rounded shadow border border-gray-100 dark:border-slate-700 p-[1px] text-gray-500">
                                 <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
                               </div>
                            </div>
                         );
                      }
                      return logoSrc ? (
                        <img key={i} src={logoSrc} alt={e} title={e} className="w-5 h-5 rounded object-contain bg-white border border-gray-100 dark:border-slate-700 shadow-sm cursor-pointer hover:scale-110 transition-transform" />
                      ) : (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-500/20 rounded">{e}</span>
                      );
                    })}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap min-w-[160px]">
                  <div className="mb-2">{getStatusBadge(task)}</div>
                  {(task.status === 'running' || task.status === 'pending') && (
                    <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1.5 mt-2">
                      <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${task.progress || 0}%` }}></div>
                    </div>
                  )}
                  {(task.status === 'running' || task.status === 'pending') && <div className="text-[10px] text-gray-500 mt-1 text-right">{task.progress || 0}%</div>}
                </td>
                <td className="px-6 py-4 text-xs text-gray-500 dark:text-slate-400 space-y-1">
                  <div><span className="text-gray-400 dark:text-slate-500">创建:</span> {formatDate(task.createdAt)}</div>
                  <div><span className="text-gray-400 dark:text-slate-500">最近执行:</span> {formatDate(task.lastRunAt)}</div>
                  {task.status === 'running' && <div className="text-blue-500">已耗时: {getElapsedTime(task)}</div>}
                </td>
                <td className="px-6 py-4 text-right">
                  <button 
                    onClick={() => setSelectedTask(task)}
                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 text-sm font-medium"
                  >
                    查看详情
                  </button>
                  {showDeleted ? (
                    <span className="ml-3 inline-flex items-center space-x-3 border-l border-gray-200 dark:border-slate-700 pl-3">
                      <button onClick={(e) => { e.stopPropagation(); restoreTask(task.id); }} className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-500 dark:hover:text-emerald-400 text-sm font-medium">恢复</button>
                      <button onClick={(e) => { e.stopPropagation(); hardDeleteTask(task.id); }} className="text-red-600 hover:text-red-800 dark:text-red-500 dark:hover:text-red-400 text-sm font-medium">彻底删除</button>
                    </span>
                  ) : (
                    <>
                      
                  {task.type === 'scheduled' ? (
                    <span className="ml-3 inline-flex items-center space-x-3 border-l border-gray-200 dark:border-slate-700 pl-3">
                      {task.status === 'scheduled' && (
                        <button onClick={(e) => { e.stopPropagation(); updateTaskStatus(task.id, 'paused'); }} className="text-amber-600 hover:text-amber-800 dark:text-amber-500 dark:hover:text-amber-400 text-sm font-medium">暂停</button>
                      )}
                      {(task.status === 'paused' || task.status === 'completed' || task.status === 'stopped' || task.status === 'failed') && (
                        <button onClick={(e) => { e.stopPropagation(); updateTaskStatus(task.id, 'scheduled'); }} className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-500 dark:hover:text-emerald-400 text-sm font-medium">开启</button>
                      )}
                      {(task.status === 'scheduled' || task.status === 'paused' || task.status === 'running') && (
                        <button onClick={(e) => { e.stopPropagation(); updateTaskStatus(task.id, 'stopped'); }} className="text-rose-500 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 text-sm font-medium">终止</button>
                      )}
                    </span>
                  ) : (
                    (task.status === 'running' || task.status === 'pending') && (
                      <span className="ml-3 inline-flex items-center space-x-3 border-l border-gray-200 dark:border-slate-700 pl-3">
                        {user?.role === 'ADMIN' && (
                          <button onClick={(e) => { e.stopPropagation(); forceCompleteTask(task.id); }} className="text-orange-500 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 text-sm font-medium">强制结单</button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); updateTaskStatus(task.id, 'stopped'); }} className="text-rose-500 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-300 text-sm font-medium">终止</button>
                      </span>
                    )
                  )}
                
                      <span className="ml-3 inline-flex items-center space-x-3 border-l border-gray-200 dark:border-slate-700 pl-3">
                        <button onClick={(e) => { e.stopPropagation(); softDeleteTask(task.id); }} className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 text-sm font-medium" title="移入回收站">删除</button>
                      </span>
                    </>
                  )}
                  </td>
              </tr>
                {childrenMap[task.id] && childrenMap[task.id].length > 0 && (
                  <tr className="bg-gray-50/30 dark:bg-slate-800/10 border-b-0 border-t-0">
                    <td colSpan="6" className="py-2 px-6">
                      <div 
                        className="flex items-center text-xs text-indigo-600 dark:text-indigo-400 cursor-pointer select-none hover:text-indigo-800 dark:hover:text-indigo-300 w-max"
                        onClick={() => toggleExpand(task.id)}
                      >
                        <ChevronRight size={14} className={`mr-1 transition-transform ${expandedTasks[task.id] ? 'rotate-90' : ''}`} />
                        {expandedTasks[task.id] ? '收起执行记录' : `展开 ${childrenMap[task.id].length} 条执行记录`}
                      </div>
                    </td>
                  </tr>
                )}
                {expandedTasks[task.id] && childrenMap[task.id] && childrenMap[task.id].map(child => (
                  <tr key={child.taskNo || child.id} className="bg-indigo-50/20 dark:bg-indigo-500/5 hover:bg-indigo-50/40 dark:hover:bg-indigo-500/10 transition-colors border-l-4 border-l-indigo-300 dark:border-l-indigo-600/50">
                    <td className="px-6 py-3 pl-10">
                      <div onClick={() => setSelectedTask(child)} className="font-mono text-sm text-indigo-600 dark:text-indigo-400 cursor-pointer hover:underline">{child.taskNo || child.id}</div>
                      <div className="text-gray-400 dark:text-slate-500 text-xs mt-1">子任务执行</div>
                    </td>
                    <td className="px-6 py-3">
                      <span className="inline-flex items-center text-gray-500 dark:text-slate-400 bg-gray-100/50 dark:bg-slate-700/50 px-2 py-0.5 rounded text-xs w-max whitespace-nowrap">调度执行</span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex flex-wrap gap-1 mb-1.5 opacity-80">
                        {(getArrayFromField(child.engines || child.targetEngines) || []).map((e, i) => {
                          const normalizedEngine = Object.keys(engineLogos).find(key => key.toLowerCase() === (e || "").toLowerCase()) || e;
                          const logoSrc = engineLogos[normalizedEngine];
                          if (e === '豆包(PC)' || e === '豆包' || e === '腾讯元宝(PC)' || e === '腾讯元宝' || e === '通义千问(PC)' || e === '通义千问') {
                             return (
                                <div key={i} className="relative inline-flex flex-col items-center group">
                                   <img src={logoSrc} alt={e} title={e} className="w-4 h-4 rounded object-contain bg-white/80 border border-gray-100 dark:border-slate-700 shadow-sm" />
                                   <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-800 rounded shadow border border-gray-100 dark:border-slate-700 p-[1px] text-gray-500">
                                     <svg className="w-2 h-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
                                   </div>
                                </div>
                             );
                          }
                          if (e === '豆包(MB)' || e === '腾讯元宝(MB)' || e === '通义千问(MB)') {
                             return (
                                <div key={i} className="relative inline-flex flex-col items-center group">
                                   <img src={logoSrc} alt={e} title={e} className="w-4 h-4 rounded object-contain bg-white/80 border border-gray-100 dark:border-slate-700 shadow-sm" />
                                   <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-800 rounded shadow border border-gray-100 dark:border-slate-700 p-[1px] text-gray-500">
                                     <svg className="w-2 h-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
                                   </div>
                                </div>
                             );
                          }
                          return logoSrc ? (
                            <img key={i} src={logoSrc} alt={e} title={e} className="w-4 h-4 rounded object-contain bg-white/80 border border-gray-100 dark:border-slate-700 shadow-sm" />
                          ) : (
                            <span key={i} className="text-[10px] px-1 py-0.5 bg-gray-50 text-gray-500 dark:bg-slate-800 dark:text-slate-400 border border-gray-100 dark:border-slate-700 rounded">{e}</span>
                          );
                        })}
                      </div>
                      
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap min-w-[160px]">
                      <div className="mb-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                          child.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20' :
                          child.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20' :
                          child.status === 'running' ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20' :
                          (child.status === 'pending' && child.fetchMode === 'rpa') ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20' :
                          'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20'
                        }`}>
                          {child.status === 'completed' && <CheckCircle2 size={10} className="mr-1" />}
                          {child.status === 'failed' && <XCircle size={10} className="mr-1" />}
                          {(child.status === 'running' || child.status === 'pending') && <Activity size={10} className="mr-1 animate-pulse" />}
                          {child.status === 'completed' ? '已完成' : 
                           child.status === 'failed' ? '失败' : 
                           child.status === 'running' ? '抓取中' : 
                           (child.status === 'pending' && child.fetchMode === 'rpa') ? '处理中' :
                           (child.status === 'pending') ? '抓取中' : '等待中'}
                        </span>
                      </div>
                      {(child.status === 'running' || child.status === 'pending') && (
                        <>
                          <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1.5 mt-2">
                            <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${child.progress || 0}%` }}></div>
                          </div>
                          <div className="text-[10px] text-gray-500 mt-1 text-right">{`${child.progress || 0}%`}</div>
                        </>
                      )}
                    </td>
                    <td className="px-6 py-3 text-xs text-gray-500 dark:text-slate-400 space-y-1">
                      <div><span className="text-gray-400 dark:text-slate-500">创建:</span> {formatDate(child.createdAt)}</div>
                      {child.status === 'running' && <div className="text-blue-500">已耗时: {getElapsedTime(child)}</div>}
                    </td>
                    <td className="px-6 py-3 text-right">
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex justify-between items-center px-6 py-4 border-t border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800/40">
            <span className="text-sm text-gray-500 dark:text-slate-400">
              共 {allParentTasks.length} 个任务，第 {currentPage} / {totalPages} 页
            </span>
            <div className="flex space-x-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                className="px-3 py-1.5 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                上一页
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                className="px-3 py-1.5 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New Task Drawer/Modal Overlay */}
      {isDrawerOpen && (
        <div className="fixed inset-0 z-50 bg-gray-900/40 dark:bg-black/60 backdrop-blur-sm flex justify-end transition-opacity">
          <div className="w-[500px] h-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
            <div className="px-6 py-5 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900">
              <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 flex items-center">
                <Play className="w-5 h-5 mr-2 text-indigo-600 dark:text-indigo-400" />
                发起 SEO 抓取任务
              </h2>
              <button onClick={() => setIsDrawerOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="px-6 pt-4 border-b border-gray-100 dark:border-slate-800 flex space-x-6">
              <button 
                onClick={() => setImportTab('api')}
                className={`pb-3 text-sm font-medium transition-colors border-b-2 ${importTab === 'api' ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
              >
                新建 API 调度任务
              </button>
              <button 
                onClick={() => setImportTab('rawdata')}
                className={`pb-3 text-sm font-medium transition-colors border-b-2 ${importTab === 'rawdata' ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200'}`}
              >
                导入明悉 Rawdata
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              
              {importTab === 'rawdata' ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-slate-200 mb-2">1. 关联品牌项目 <span className="text-red-500">*</span></label>
                    <select 
                      value={newTaskForm.projectId} 
                      onChange={(e) => setNewTaskForm({...newTaskForm, projectId: e.target.value})}
                      className="w-full h-11 px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-slate-200 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm cursor-pointer"
                    >
                      <option value="">-- 请选择关联的品牌项目 --</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}{p.subBrandName ? ` - ${p.subBrandName}` : ''}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-2">导入的数据将作为该项目的数据资产进行大盘聚合和竞品分析。</p>
                  </div>
                  <hr className="border-gray-100 dark:border-slate-800" />
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-slate-200 mb-2">2. 数据产生时间 <span className="text-red-500">*</span></label>
                    <input 
                      type="date"
                      value={newTaskForm.dataDate || ''}
                      onChange={(e) => setNewTaskForm({...newTaskForm, dataDate: e.target.value})}
                      className="w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-slate-200 text-base rounded-xl focus:ring-2 focus:ring-indigo-500 py-3.5 px-4 outline-none shadow-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">用于将导入的历史数据正确放置在趋势时间线上。默认为当天。</p>
                  </div>
                  <hr className="border-gray-100 dark:border-slate-800" />
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-slate-200 mb-2">3. 上传数据报告 <span className="text-red-500">*</span></label>
                    <div 
                      className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-slate-700 border-dashed rounded-xl bg-gray-50 dark:bg-slate-800/50 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                          setImportFile(e.dataTransfer.files[0]);
                        }
                      }}
                    >
                      <div className="space-y-1 text-center">
                        <FileText className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600 dark:text-slate-400 justify-center mt-4">
                          <label htmlFor="file-upload" className="relative cursor-pointer bg-white dark:bg-slate-700 rounded-md font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 px-2 py-1 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                            <span>选择 Excel 文件</span>
                            <input id="file-upload" name="file-upload" type="file" className="sr-only" accept=".xlsx,.xls" onChange={(e) => setImportFile(e.target.files[0])} />
                          </label>
                          <p className="pl-2 pt-1">或直接拖拽到此处</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {importFile ? importFile.name : "支持 .xlsx 或 .xls 格式 (明悉原始数据)"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
              {/* Project Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-slate-200 mb-2">1. 选择品牌项目 <span className="text-red-500">*</span></label>
                <select 
                  value={newTaskForm.projectId} 
                  onChange={(e) => setNewTaskForm({...newTaskForm, projectId: e.target.value})}
                  className="w-full h-11 px-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-slate-200 text-sm rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none shadow-sm cursor-pointer"
                >
                  <option value="">-- 请选择要抓取的品牌项目 --</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}{p.subBrandName ? ` - ${p.subBrandName}` : ''}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">选择项目后，系统将自动读取该项目下配置的监控词表和竞品。</p>
              </div>

              <hr className="border-gray-100 dark:border-slate-800" />

              {/* Task Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-slate-200 mb-3">2. 任务执行方式 <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    onClick={() => setNewTaskForm({...newTaskForm, taskType: 'one-time'})}
                    className={`cursor-pointer rounded-xl border p-4 transition-all ${
                      newTaskForm.taskType === 'one-time' 
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 ring-1 ring-indigo-500' 
                        : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    <div className="flex items-center mb-1">
                      <Play size={18} className={newTaskForm.taskType === 'one-time' ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'} />
                      <span className={`ml-2 font-medium text-sm ${newTaskForm.taskType === 'one-time' ? 'text-indigo-900 dark:text-indigo-200' : 'text-gray-700 dark:text-slate-300'}`}>立即单次执行</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-slate-400 mt-1 pl-6">适合手动诊断和快速排查数据变化</div>
                  </div>
                  
                  <div 
                    onClick={() => setNewTaskForm({...newTaskForm, taskType: 'scheduled'})}
                    className={`cursor-pointer rounded-xl border p-4 transition-all ${
                      newTaskForm.taskType === 'scheduled' 
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-500/10 ring-1 ring-purple-500' 
                        : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    <div className="flex items-center mb-1">
                      <Calendar size={18} className={newTaskForm.taskType === 'scheduled' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400'} />
                      <span className={`ml-2 font-medium text-sm ${newTaskForm.taskType === 'scheduled' ? 'text-purple-900 dark:text-purple-200' : 'text-gray-700 dark:text-slate-300'}`}>添加周期计划</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-slate-400 mt-1 pl-6">适合建立长期的日常防守监控网</div>
                  </div>
                </div>
                
                {newTaskForm.taskType === 'scheduled' && (
                  <div className="mt-4 bg-purple-50/50 dark:bg-purple-900/10 p-4 rounded-xl border border-purple-100 dark:border-purple-800/30 animate-in slide-in-from-top-2 duration-200">
                    <label className="block text-xs font-medium text-purple-800 dark:text-purple-300 mb-2">执行频率设置</label>
                    <div className="space-y-3">
                      <select 
                        value={newTaskForm.freq}
                        onChange={(e) => setNewTaskForm({...newTaskForm, freq: e.target.value})}
                        className="w-full bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-700 text-gray-900 dark:text-slate-200 text-sm rounded-lg p-2.5 outline-none"
                      >
                        <option value="daily">每天执行</option>
                        <option value="weekly">每周执行</option>
                        <option value="monthly">每月执行</option>
                      </select>
                      
                      <div className="flex gap-2">
                        {newTaskForm.freq === 'weekly' && (
                          <select
                            value={newTaskForm.freqDayOfWeek}
                            onChange={(e) => setNewTaskForm({...newTaskForm, freqDayOfWeek: e.target.value})}
                            className="flex-1 bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-700 text-gray-900 dark:text-slate-200 text-sm rounded-lg p-2.5 outline-none"
                          >
                            <option value="1">星期一</option>
                            <option value="2">星期二</option>
                            <option value="3">星期三</option>
                            <option value="4">星期四</option>
                            <option value="5">星期五</option>
                            <option value="6">星期六</option>
                            <option value="0">星期日</option>
                          </select>
                        )}
                        {newTaskForm.freq === 'monthly' && (
                          <select
                            value={newTaskForm.freqDayOfMonth}
                            onChange={(e) => setNewTaskForm({...newTaskForm, freqDayOfMonth: e.target.value})}
                            className="flex-1 bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-700 text-gray-900 dark:text-slate-200 text-sm rounded-lg p-2.5 outline-none"
                          >
                            {[...Array(31)].map((_, i) => (
                              <option key={i+1} value={i+1}>{i+1} 号</option>
                            ))}
                          </select>
                        )}
                        <select
                          value={newTaskForm.freqTime}
                          onChange={(e) => setNewTaskForm({...newTaskForm, freqTime: e.target.value})}
                          className="flex-1 bg-white dark:bg-slate-800 border border-purple-200 dark:border-purple-700 text-gray-900 dark:text-slate-200 text-sm rounded-lg p-2.5 outline-none"
                        >
                          {[...Array(24)].map((_, i) => {
                            const hr = i.toString().padStart(2, '0');
                            return (
                              <option key={hr} value={hr + ':00'}>{hr}:00</option>
                            )
                          })}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <hr className="border-gray-100 dark:border-slate-800" />

              {/* Target Filtering (Placeholder) */}
              
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-slate-200 mb-2">3. 抓取范围 (高级筛选)</label>
                <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 border border-gray-100 dark:border-slate-700">
                  <div className="text-sm text-gray-600 dark:text-slate-400 mb-3">默认使用项目下配置的所有「可用引擎」与「所有人设」。您也可以在此单独指定本次抓取的特定目标：</div>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-slate-300">目标引擎</span>
                        <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">{newTaskForm.engines.length === 0 ? '全部可用引擎 (默认)' : `已选 ${newTaskForm.engines.length} 个`}</span>
                      </div>
                      
                      
                      <div className="flex flex-wrap gap-2 mt-3 items-center">
                        {['Deepseek', 'Kimi', '文心一言', '豆包', '腾讯元宝', '通义千问', '小红书AI', '抖音AI', '百度AI搜索'].map(baseEngine => {
                          const isXhs = baseEngine === '百度AI搜索';
                          const isGrouped = ['豆包', '腾讯元宝', '通义千问'].includes(baseEngine);
                          
                          if (isGrouped) {
                            const pcName = baseEngine + '(PC)';
                            const mbName = baseEngine + '(MB)';
                            const isPCSelected = newTaskForm.engines.length === 0 || newTaskForm.engines.includes(pcName);
                            const isMBSelected = newTaskForm.engines.length === 0 || newTaskForm.engines.includes(mbName);
                            
                            return (
                              <div key={baseEngine} className="flex flex-col gap-1.5 p-2 bg-gray-50 dark:bg-slate-800/50 rounded-lg border border-gray-100 dark:border-slate-700 min-w-[30%] flex-1">
                                <div className="text-xs font-medium text-gray-600 dark:text-slate-400 flex items-center gap-1.5">
                                  {engineLogos[baseEngine] && <img src={engineLogos[baseEngine]} alt={baseEngine} className="w-3.5 h-3.5 object-contain rounded-sm" />}
                                  {baseEngine}
                                </div>
                                <div className="flex gap-1.5 w-full mt-auto">
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      let newEngines = [...newTaskForm.engines];
                                      if (newEngines.length === 0) newEngines = availableEngines.filter(e => e !== pcName && e !== '百度AI搜索');
                                      else if (newEngines.includes(pcName)) { if (newEngines.length === 1) { return; } newEngines = newEngines.filter(e => e !== pcName); }
                                      else newEngines.push(pcName);
                                      setNewTaskForm({...newTaskForm, engines: newEngines});
                                    }}
                                    className={`px-1 py-1 text-[11px] rounded border transition-colors flex-1 flex justify-center items-center whitespace-nowrap ${isPCSelected ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-500/20 dark:border-indigo-500/30 dark:text-indigo-300' : 'bg-white border-gray-200 text-gray-500 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400'}`}
                                  >
                                    PC端
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      let newEngines = [...newTaskForm.engines];
                                      if (newEngines.length === 0) newEngines = availableEngines.filter(e => e !== mbName && e !== '百度AI搜索');
                                      else if (newEngines.includes(mbName)) { if (newEngines.length === 1) { return; } newEngines = newEngines.filter(e => e !== mbName); }
                                      else newEngines.push(mbName);
                                      setNewTaskForm({...newTaskForm, engines: newEngines});
                                    }}
                                    className={`px-1 py-1 text-[11px] rounded border transition-colors flex-1 flex justify-center items-center whitespace-nowrap ${isMBSelected ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-400' : 'bg-white border-gray-200 text-gray-500 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400'}`}
                                  >
                                    MB端
                                  </button>
                                </div>
                              </div>
                            );
                          }

                          const isSelected = !isXhs && (newTaskForm.engines.length === 0 || newTaskForm.engines.includes(baseEngine));
                          return (
                            <button
                              key={baseEngine}
                              disabled={isXhs}
                              title={isXhs ? "暂未开放" : ""}
                              onClick={(e) => {
                                e.preventDefault();
                                let newEngines = [...newTaskForm.engines];
                                if (newEngines.length === 0) {
                                  newEngines = availableEngines.filter(e => e !== baseEngine && e !== '百度AI搜索');
                                } else {
                                  if (newEngines.includes(baseEngine)) { if (newEngines.length === 1) { return; } newEngines = newEngines.filter(e => e !== baseEngine); } else {
                                    newEngines.push(baseEngine);
                                  }
                                }
                                setNewTaskForm({...newTaskForm, engines: newEngines});
                              }}
                              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors flex items-center justify-center gap-1.5 min-w-[30%] flex-1 ${
                                isXhs ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed dark:bg-slate-800 dark:border-slate-700 dark:text-slate-500 opacity-60' :
                                isSelected ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-500/20 dark:border-indigo-500/30 dark:text-indigo-300' : 'bg-white border-gray-200 text-gray-500 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400'}`}
                            >
                              {engineLogos[baseEngine] && <img src={engineLogos[baseEngine]} alt={baseEngine} className="w-3.5 h-3.5 object-contain rounded-sm" />}
                              {baseEngine}
                            </button>
                          );
                        })}
</div>
                      { (newTaskForm.engines.includes('小红书') || newTaskForm.engines.includes('小红书AI')) && (
                        <div className="mt-3 p-3 bg-gray-50 dark:bg-slate-800/50 rounded-lg border border-gray-100 dark:border-slate-700">
                          <div className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">小红书抓取偏好</div>
                          <div className="flex gap-4">
                            <label className="flex items-center text-xs cursor-pointer text-gray-700 dark:text-slate-300">
                              <input type="radio" name="xhsMode" value="2" checked={newTaskForm.xhsMode === '2'} onChange={e => setNewTaskForm({...newTaskForm, xhsMode: e.target.value})} className="mr-2 text-indigo-600" />
                              AIO和笔记都抓 (推荐)
                            </label>
                            <label className="flex items-center text-xs cursor-pointer text-gray-700 dark:text-slate-300">
                              <input type="radio" name="xhsMode" value="0" checked={newTaskForm.xhsMode === '0'} onChange={e => setNewTaskForm({...newTaskForm, xhsMode: e.target.value})} className="mr-2 text-indigo-600" />
                              只抓AIO总结
                            </label>
                            <label className="flex items-center text-xs cursor-pointer text-gray-700 dark:text-slate-300">
                              <input type="radio" name="xhsMode" value="1" checked={newTaskForm.xhsMode === '1'} onChange={e => setNewTaskForm({...newTaskForm, xhsMode: e.target.value})} className="mr-2 text-indigo-600" />
                              只抓传统笔记
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {(() => {
                let kwCount = 0;
                let engCount = 0;
                let perCount = 0;
                if (newTaskForm.projectId) {
                  const proj = projects.find(p => p.id === parseInt(newTaskForm.projectId));
                  if (proj) {
                    if (proj.seoKeywords && proj.seoKeywords.length > 0) {
                      kwCount = proj.seoKeywords.length;
                    } else if (proj.keywordPackages && proj.keywordPackages.length > 0) {
                      kwCount = proj.keywordPackages.reduce((total, pkg) => total + (pkg.items ? pkg.items.length : 0), 0);
                    } else if (proj.themes && proj.themes.length > 0) {
                      kwCount = proj.themes.reduce((acc, t) => acc + (t.subThemes?.length || 1), 0);
                    } else if (proj.classifiedKeywords) {
                      try {
                         const parsed = JSON.parse(proj.classifiedKeywords);
                         kwCount = Array.isArray(parsed) ? parsed.length : 0;
                      } catch(e){}
                    }
                    if (kwCount === 0 && proj.keywords) {
                      kwCount = proj.keywords.split(/\n+/).filter(k => k.trim()).length;
                    }
                    
                    engCount = newTaskForm.engines.length === 0 ? availableEngines.filter(e => e !== '百度AI搜索').length : newTaskForm.engines.length;
                    perCount = newTaskForm.personas.length === 0 ? 1 : newTaskForm.personas.length;
                  }
                }
                const total = kwCount * engCount * perCount;
                const mins = Math.ceil(total / 1.5) + 1; // 假设每分钟处理1.5个请求(含大模型生成耗时与休眠)，并增加1分钟基础缓冲

                return (
                  <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 p-4 rounded-xl text-sm border border-blue-100 dark:border-blue-800/30">
                    <p className="font-semibold mb-1">预计消耗提示：</p>
                    <p className="text-xs opacity-90">
                      当前选择的项目预估将产生 <strong className="font-bold">{total}</strong> 次检索会话 
                      ({kwCount}个词 × {engCount}个引擎{perCount > 1 ? ` × ${perCount}个人设` : ''})，
                      预计耗时 <strong className="font-bold">{mins} 分钟</strong>。
                      {newTaskForm.taskType === 'one-time' ? ' 执行时将扣除相应积分。' : ' 每次定时触发时将扣除相应积分。'}
                    </p>
                  </div>
                );
              })()}

              {true && (
                <>
                  <hr className="border-gray-100 dark:border-slate-800" />
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-slate-200 mb-2">4. 抓取高级配置</label>
                    <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-4 border border-gray-100 dark:border-slate-700">
                      
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 dark:text-slate-300">抓取深度 (条数)</span>
                          <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">当前: {newTaskForm.seoDepth || 10} 条</span>
                        </div>
                        <select
                          value={newTaskForm.seoDepth || 10}
                          onChange={(e) => setNewTaskForm({...newTaskForm, seoDepth: Number(e.target.value)})}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="10">10条 (极速)</option>
                          <option value="20">20条</option>
                          <option value="30">30条</option>
                          <option value="40">40条</option>
                          <option value="50">50条 (深度)</option>
                        </select>
                      </div>

                      {/* XHS Sort Options (Only show if 小红书 is selected or no engines selected implies all) */}
                      {(newTaskForm.engines.length === 0 || newTaskForm.engines.some(e => e.includes('小红书') || e.includes('xhs'))) && (
                        <div className="mb-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                          <div className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">小红书排序方式</div>
                          <div className="flex gap-4 flex-wrap">
                            <label className="flex items-center text-xs cursor-pointer text-gray-700 dark:text-slate-300">
                              <input type="radio" name="xhsSort" value="100" checked={newTaskForm.xhsSort === '100' || !newTaskForm.xhsSort} onChange={e => setNewTaskForm({...newTaskForm, xhsSort: e.target.value})} className="mr-2 text-indigo-600" />
                              综合
                            </label>
                            <label className="flex items-center text-xs cursor-pointer text-gray-700 dark:text-slate-300">
                              <input type="radio" name="xhsSort" value="001" checked={newTaskForm.xhsSort === '001'} onChange={e => setNewTaskForm({...newTaskForm, xhsSort: e.target.value})} className="mr-2 text-indigo-600" />
                              最新发布
                            </label>
                            <label className="flex items-center text-xs cursor-pointer text-gray-700 dark:text-slate-300">
                              <input type="radio" name="xhsSort" value="010" checked={newTaskForm.xhsSort === '010'} onChange={e => setNewTaskForm({...newTaskForm, xhsSort: e.target.value})} className="mr-2 text-indigo-600" />
                              最多点赞
                            </label>
                            <label className="flex items-center text-xs cursor-pointer text-gray-700 dark:text-slate-300">
                              <input type="radio" name="xhsSort" value="002" checked={newTaskForm.xhsSort === '002'} onChange={e => setNewTaskForm({...newTaskForm, xhsSort: e.target.value})} className="mr-2 text-indigo-600" />
                              最多评论
                            </label>
                            <label className="flex items-center text-xs cursor-pointer text-gray-700 dark:text-slate-300">
                              <input type="radio" name="xhsSort" value="003" checked={newTaskForm.xhsSort === '003'} onChange={e => setNewTaskForm({...newTaskForm, xhsSort: e.target.value})} className="mr-2 text-indigo-600" />
                              最多收藏
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                    
                                                            <div className="mt-4 flex items-center opacity-60">
                      <input
                        id="needScreenshot"
                        type="checkbox"
                        disabled={true}
                        checked={false}
                        className="h-4 w-4 text-indigo-300 border-gray-200 rounded cursor-not-allowed dark:border-slate-700 dark:bg-slate-800"
                      />
                      <label htmlFor="needScreenshot" className="ml-2 block text-sm text-gray-400 dark:text-slate-500 cursor-not-allowed">
                        需要抓取的时候对搜索结果进行截图 (SEO 版暂时锁定，维护中)
                      </label>
                    </div>
                  </div>
                </>
              )}
              </>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/50 flex justify-end space-x-3">
              <button 
                onClick={() => setIsDrawerOpen(false)}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
              >
                取消
              </button>
              <button 
                onClick={() => {
                  if (importTab === 'rawdata') {
                    handleImportSubmit();
                  } else {
                    handleSubmitTask();
                  }
                }}
                className={`px-5 py-2.5 text-sm font-medium text-white rounded-xl shadow-md transition-colors flex items-center ${
                  newTaskForm.projectId === '' 
                    ? 'bg-indigo-400 cursor-not-allowed opacity-70' 
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
                disabled={newTaskForm.projectId === '' || isSubmitting}
              >
                <Play size={16} className="mr-2" />
                {importTab === 'rawdata' ? '立即开始导入' : (newTaskForm.taskType === 'scheduled' ? '保存巡检计划' : '立即开始抓取')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Details Drawer/Modal Overlay */}
      {selectedTask && (
        <div className="fixed inset-0 z-50 bg-gray-900/40 dark:bg-black/60 backdrop-blur-sm flex justify-end transition-opacity">
          <div className="w-[800px] h-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 flex items-center">
                  任务详情
                </h2>
                <p className="text-sm text-gray-500 mt-1">任务编号：{selectedTask.taskNo || selectedTask.id} / 任务ID：{28000000 + selectedTask.id}</p>
                <p className="text-sm text-gray-500 mt-1">关联项目: {selectedTask.projectName}</p>
              </div>
              <button onClick={() => setSelectedTask(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto bg-gray-50/30 dark:bg-slate-900/30">
              {/* Status Banner */}
              <div className="p-6 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-800/50">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex space-x-6">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">当前状态</div>
                      <div>{getStatusBadge(selectedTask)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">执行类型</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-slate-200">
                        {selectedTask.type === 'one-time' ? '单次抓取' : `周期巡检 (${formatFrequency(selectedTask.scheduleFreq || selectedTask.frequency || '未知')})`}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">已耗时</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-slate-200">{formatDuration(selectedTask.startedAt || selectedTask.createdAt, selectedTask.completedAt)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">目标引擎</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-slate-200">{getArrayFromField(selectedTask.engines || selectedTask.targetEngines).length} 个</div>
                    </div>
                  </div>
                  
                  {selectedTask.status === 'running' && (
                    <button 
                      onClick={() => updateTaskStatus(selectedTask.id, 'stopped')}
                      className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20 rounded-lg text-sm font-medium transition-colors border border-red-100 dark:border-red-500/20"
                    >
                      紧急终止
                    </button>
                  )}
                </div>

                {/* Progress Bar Area */}
                {selectedTask.status !== 'scheduled' && (
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-medium text-gray-700 dark:text-slate-300">总体抓取进度</span>
                      <span className="font-bold text-indigo-600 dark:text-indigo-400">
                        {selectedTask.status === 'completed' ? 100 : (selectedTask.progress || 0)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                      <div 
                        className={`h-2.5 rounded-full ${selectedTask.status === 'failed' ? 'bg-red-500' : selectedTask.status === 'completed' ? 'bg-emerald-500' : 'bg-indigo-500 relative overflow-hidden'}`}
                        style={{ width: `${selectedTask.status === 'completed' ? 100 : (selectedTask.progress || 0)}%` }}
                      >
                        {selectedTask.status === 'running' && (
                          <div className="absolute top-0 left-0 right-0 bottom-0 bg-white/20 animate-[shimmer_2s_infinite]"></div>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>完成进度: {selectedTask.status === 'completed' ? 100 : (selectedTask.progress || 0)}%</span>
                      <span>{selectedTask.status === 'running' ? '正在执行中...' : (selectedTask.status === 'completed' ? '已完成' : '')}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Detail Tabs content */}
              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  {/* Left Column: Engine Status */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 flex items-center">
                      <Database className="w-4 h-4 mr-2 text-indigo-500" /> 
                      各引擎抓取矩阵
                    </h3>
                    
                    <div className="space-y-3">
                      {(taskDetail?.engineStatuses || []).length > 0 ? taskDetail.engineStatuses.map((engineStatus, idx) => {
                        const engine = {
                          name: engineStatus.engineId,
                          status: engineStatus.status,
                          progress: engineStatus.progress,
                          subIds: engineStatus.subIds || [],
                          pendingIds: engineStatus.pendingIds || [],
                          completedIds: engineStatus.completedIds || []
                        };
                        return (
                        <div key={idx} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4 shadow-sm">
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                              {engineLogos[Object.keys(engineLogos).find(key => key.toLowerCase() === engine.name?.toLowerCase()) || engine.name] && (
                                <img src={engineLogos[Object.keys(engineLogos).find(key => key.toLowerCase() === engine.name?.toLowerCase()) || engine.name]} alt={engine.name} className="w-4 h-4 rounded-sm object-contain bg-white" />
                              )}
                              <span className="font-medium text-sm text-gray-800 dark:text-slate-200">{engine.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {engine.status === 'completed' && <CheckCircle2 size={16} className="text-emerald-500" />}
                              {engine.status === 'failed' && <XCircle size={16} className="text-red-500" />}
                              {engine.status === 'running' && <Activity size={16} className="text-blue-500 animate-pulse" />}
                              {engine.status === 'pending' && <Clock size={16} className="text-gray-400" />}
                              
                              {engine.status !== 'completed' && (
                                <button 
                                  onClick={() => handleEngineFallback(selectedTask.id, engine.name)}
                                  className="ml-2 flex items-center text-xs px-2 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-800/50 rounded transition-colors"
                                  title="API 直连补足"
                                >
                                  <Zap size={12} className="mr-1" />
                                  补足
                                </button>
                              )}
                            </div>
                          </div>
                                                    <div className="w-full bg-gray-100 dark:bg-slate-700 rounded-full h-1.5 mt-3 mb-2">
                            <div 
                              className={`h-1.5 rounded-full ${engine.status === 'completed' ? 'bg-emerald-500' : engine.status === 'running' ? 'bg-blue-500' : 'bg-transparent'}`} 
                              style={{ width: `${engine.progress}%` }}
                            ></div>
                          </div>
                          
                          {engine.subIds && engine.subIds.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-1.5">
                              {engine.subIds.map(subId => {
                                const isCompleted = engine.completedIds?.includes(subId);
                                return (
                                  <span 
                                    key={subId} 
                                    className={`text-[10px] px-1.5 py-0.5 rounded border ${isCompleted ? 'bg-emerald-50 border-emerald-200 text-emerald-600 dark:bg-emerald-900/20 dark:border-emerald-800/50 dark:text-emerald-400' : 'bg-gray-50 border-gray-200 text-gray-500 dark:bg-slate-800/50 dark:border-slate-700 dark:text-slate-400'}`}
                                    title={isCompleted ? "已返回" : "等待中"}
                                  >
                                    {subId}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        );
                      }) : <div className="text-gray-500 text-sm">暂无引擎状态数据</div>}
                    </div>
                  </div>

                  {/* Right Column: Live Console Logs */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-slate-100 flex items-center">
                      <Terminal className="w-4 h-4 mr-2 text-gray-500" /> 
                      实时调度日志
                    </h3>
                    
                    <div className="bg-gray-900 rounded-lg p-4 h-[400px] overflow-y-auto text-xs font-mono text-gray-300 shadow-inner">
                      <div className="space-y-2">
                        {!taskDetail ? (
                          <div className="text-gray-500">正在加载日志...</div>
                        ) : taskDetail.logs && taskDetail.logs.length > 0 ? (
                          taskDetail.logs.map((log, i) => (
                            <div key={i} className={
                              log.level === 'error' ? 'text-red-400' :
                              log.level === 'success' ? 'text-emerald-400' :
                              log.level === 'warning' ? 'text-yellow-400' : 'text-gray-400'
                            }>
                              [{new Date(log.createdAt).toLocaleTimeString()}] {log.engineId ? `${log.engineId}: ` : '系统: '}{log.message}
                            </div>
                          ))
                        ) : (
                          <div className="text-gray-500">暂无调度日志...</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 flex justify-between items-center">
              <span className="text-xs text-gray-500">任务日志将保留 30 天</span>
              <button 
                onClick={() => setSelectedTask(null)}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 rounded-xl transition-colors"
              >
                关闭面板
              </button>
              <button 
                onClick={() => navigate(`/tasks/${selectedTask.id || selectedTask.taskNo}/geo-results`)}
                className="px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors shadow-sm ml-3 flex items-center"
              >
                查看抓取明细大盘
              </button>
            </div>
          </div>
        </div>
      )}

          </div>
  );
}
